import NumberToAbbv from "@Resources/js/filters/NumberToAbbv"
import type { Feature, Option } from "@Resources/js/laravel.d.ts"

/**
 * Price data for a specific billing interval
 */
export interface PlanPrice {
  id: string
  interval: "monthly" | "yearly"
  amount: number
  currency: string
}

/**
 * Billable type that a plan is available for
 */
export type PlanType = "user" | "team" | "organization"

/**
 * A formatted fact about a plan for display
 */
export interface PlanFact {
  content: string
  assistive?: string
}

/**
 * Labels for plan option keys
 */
const OPTION_LABELS: Record<string, string> = {
  max_aliases_per_month: "Links / mo",
  max_redirects_per_month: "Tracked Clicks / mo",
  max_branded_domains_subdomains: "Branded Domains",
  max_branded_domains: "Branded Domains",
  links_with_unlimited_clicks: "Links / mo",
}

/**
 * Raw plan data from the server
 */
export interface PlanData {
  id: string
  name: string
  group: string | null
  type: PlanType
  description: string | null
  features: Feature[]
  highlighted: string[]
  options: Partial<Record<Option, number | string | null>>
  prices: PlanPrice[]
  enterprise: boolean
  tags: string[]
}

/**
 * Plan smart object with helper methods
 */
export interface Plan extends PlanData {
  getPrice: (interval: "monthly" | "yearly") => PlanPrice | null
  getMonthlyPrice: () => PlanPrice | null
  getYearlyPrice: () => PlanPrice | null
  isCurrentPlan: (subscriptionName: string | null) => boolean
  hasFeature: (feature: Feature) => boolean
  getOption: (option: Option) => number | string | null
  formattedTitle: () => string
  formattedMonthlyPrice: () => string
  formattedYearlyPrice: () => string
  formattedMonthlyEquivalentOfYearlyPrice: () => string
  formattedHighlightedValue: () => string
  volume: () => number | null
  formattedShortVolume: () => string
  formattedLongVolume: () => string
  getFacts: () => PlanFact[]
}

/**
 * Creates a Plan smart object from raw server data.
 *
 * @param data The raw plan data from the server
 */
export default function PlanModel(data: PlanData): Plan {
  const that: Plan = {
    ...data,

    /**
     * Returns the price for the given billing interval
     */
    getPrice(interval: "monthly" | "yearly"): PlanPrice | null {
      return data.prices.find((p) => p.interval === interval) ?? null
    },

    /**
     * Returns the monthly price
     */
    getMonthlyPrice(): PlanPrice | null {
      return that.getPrice("monthly")
    },

    /**
     * Returns the yearly price
     */
    getYearlyPrice(): PlanPrice | null {
      return that.getPrice("yearly")
    },

    /**
     * Returns true if this plan matches the current subscription
     */
    isCurrentPlan(subscriptionName: string | null): boolean {
      if (!subscriptionName) {
        return false
      }
      return data.name === subscriptionName
    },

    /**
     * Returns true if this plan has the given feature
     */
    hasFeature(feature: Feature): boolean {
      return data.features.includes(feature)
    },

    /**
     * Returns the value of the given option, or null if not set
     */
    getOption(option: Option): number | string | null {
      return data.options[option] ?? null
    },

    /**
     * Returns a formatted display title for the plan
     */
    formattedTitle(): string {
      return data.name
        .replace(/(\d+)([km]?)$/i, " $1$2")
        .split(/[-_]/)
        .map((word) => word.trim())
        .filter((word) => word.length > 0)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    },

    /**
     * Returns the formatted monthly price string (e.g. "$29")
     */
    formattedMonthlyPrice(): string {
      const price = that.getMonthlyPrice()
      if (!price) {
        return ""
      }
      return `$${Math.round(price.amount / 100)}`
    },

    /**
     * Returns the formatted yearly price string (e.g. "$290")
     */
    formattedYearlyPrice(): string {
      const price = that.getYearlyPrice()
      if (!price) {
        return ""
      }
      return `$${Math.round(price.amount / 100)}`
    },

    /**
     * Returns the formatted monthly equivalent of the yearly price (yearly / 12)
     */
    formattedMonthlyEquivalentOfYearlyPrice(): string {
      const price = that.getYearlyPrice()
      if (!price) {
        return ""
      }
      return `$${Math.round(price.amount / 100 / 12)}`
    },

    /**
     * Returns the first highlighted option value as an abbreviated string (e.g. "5K", "100K").
     * Used for slider labels in plan selection cards.
     */
    formattedHighlightedValue(): string {
      for (const key of data.highlighted) {
        const value = that.getOption(key as Option)
        if (typeof value === "number") {
          return NumberToAbbv(value)
        }
      }
      return ""
    },

    /**
     * Returns the plan's links per month, or null when the plan has no links limit or it is unlimited.
     */
    volume(): number | null {
      const value = that.getOption("max_aliases_per_month")
      return typeof value === "number" ? value : null
    },

    /**
     * Returns the plan volume abbreviated (e.g. "50K"), or an empty string when there is none.
     */
    formattedShortVolume(): string {
      const volume = that.volume()
      return volume === null ? "" : NumberToAbbv(volume)
    },

    /**
     * Returns the plan volume with grouped digits (e.g. "50,000"), or an empty string when there is none.
     */
    formattedLongVolume(): string {
      const volume = that.volume()
      return volume === null ? "" : volume.toLocaleString("en-US")
    },

    /**
     * Returns formatted facts for the plan's highlighted options.
     * Used for displaying plan features in selection cards.
     */
    getFacts(): PlanFact[] {
      const facts: PlanFact[] = []

      for (const key of data.highlighted) {
        const label = OPTION_LABELS[key]
        if (!label) {
          continue
        }

        const value = that.getOption(key as Option)

        if (value === null) {
          facts.push({ content: `Unlimited ${label}` })
        } else if (typeof value === "number") {
          facts.push({ content: `${value.toLocaleString()} ${label}` })
        }
      }

      return facts
    },
  }

  return that
}
